'use client';

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import ProductCard from '../ui/ProductCard';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import { usePageShowcases } from '../providers/PageShowcasesProvider';
import { useCart } from '../contexts/CartContext';
import { getStoredStoreId } from '@/lib/store-storage';
import { mergeCannabisFieldsFromAvailability } from '@/lib/product-potency-merge';
import { parseProduct } from '@shared/lib/product-parser';
import { Button } from '@/components';
import { SEGMENTED_HEIGHT, SEGMENTED_TRACK, segmentClasses } from '@/components/ui/segmented';

type SlotKey =
  | 'afterHero'
  | 'afterPopularBrands'
  | 'afterCommunity'
  | 'afterBenefits'
  | 'afterFAQ'
  | 'afterExploreSection'
  | 'afterLocations'
  | 'recentDeals'
  | 'productSelection'
  | 'memberPicks';

export type ShowcaseLayout = '1x4' | '2x4';

/** Cards per row on desktop, and therefore per page in the one-row layout. */
const ROW_SIZE = 4;
/**
 * How deep the one-row pool goes.
 *
 * Eight, the same count two rows shows at once, split into two pages of four.
 * The layout used to hard-limit to exactly the four on screen, so a slot
 * matching sixty products was indistinguishable from one matching four and the
 * arrows had nowhere to go.
 *
 * Kept in step with showcasePoolLimit in backend/src/lib/showcase-pool.ts,
 * which decides how many the API sends.
 */
const SINGLE_ROW_POOL = ROW_SIZE * 2;

/**
 * Height of every control on the header row: both pager arrows and Shop all.
 * They sit side by side, so a size that differs between them reads as a
 * mistake.
 */
const ROW_CONTROL = `${SEGMENTED_HEIGHT} shrink-0 rounded-lg`;

export type FilterGroup = {
  field: string;
  thresholds: number[];
};

export type ShowcaseSlot = {
  slotKey: string;
  title: string | null;
  collectionId: string | null;
  collectionHandle?: string | null;
  layout: ShowcaseLayout;
  productLimit: number;
  sortOrder: number;
  showTitle: boolean;
  filterGroup?: FilterGroup;
  products: any[];
  productsByThreshold?: Record<string, any[]>;
};

export function toCardItem(product: any, storeId: number | null) {
  const groupProducts = Array.isArray(product?.groupProducts) ? product.groupProducts : [];
  const baseAvail =
    Array.isArray(product?.availabilities) && product.availabilities.length > 0
      ? product.availabilities[0]
      : null;

  // Process sizeOptions - enhance with full details from groupProducts if needed
  let sizeOptions = Array.isArray(product?.sizeOptions)
    ? product.sizeOptions
        .map((opt: any) => {
          // Find matching product in groupProducts for additional details
          const p = groupProducts.find((gp: any) => 
            gp.id === opt.productId || 
            gp.productId === opt.productId ||
            (gp.product && (gp.product.id === opt.productId || gp.product.productId === opt.productId))
          );
          
          // Try to find availability for this product/option
          let pAvail = null;
          if (p?.availabilities) {
            pAvail = Array.isArray(p.availabilities) && p.availabilities.length > 0 
              ? (storeId 
                  ? p.availabilities.find((a: any) => a.covaLocationId === storeId) || p.availabilities[0]
                  : p.availabilities[0])
              : null;
          }
          
          // Get product details - handle both direct product object and nested product.product
          const pProduct = p?.product || p;
          
          return {
            packageId: opt.packageId || pAvail?.packageId || p?.packageId || baseAvail?.packageId || product.id,
            sizeOrWeight: opt.sizeOrWeight || p?.sizeOrWeight || null,
            price: typeof opt.price === 'number' ? opt.price : Number(opt.price || p?.price || 0),
            compareAtPrice: typeof opt.compareAtPrice === 'number' ? opt.compareAtPrice : (opt.compareAtPrice !== null && opt.compareAtPrice !== undefined ? Number(opt.compareAtPrice) : (pAvail?.compareAtPrice ? Number(pAvail.compareAtPrice) : null)),
            unitCost: typeof opt.unitCost === 'number' ? opt.unitCost : (opt.unitCost !== null && opt.unitCost !== undefined ? Number(opt.unitCost) : (pAvail?.unitCost ? Number(pAvail.unitCost) : null)),
            inStock: typeof opt.inStock === 'number' ? opt.inStock : Number(opt.inStock || pAvail?.inStock || p?.inStock || 0),
            productId: opt.productId || p?.productId || p?.id,
            variationId: opt.variationId || p?.variationId || null,
            productSlug: opt.productSlug || p?.slug || pProduct?.slug || product?.slug || null,
            heroShotUri: opt.heroShotUri || p?.heroShotUri || pProduct?.heroShotUri || product?.heroShotUri || null,
            shortDescription: opt.shortDescription || p?.shortDescription || pProduct?.shortDescription || product?.shortDescription || null,
            longDescription: opt.longDescription || p?.longDescription || pProduct?.longDescription || product?.longDescription || null,
            netContent: opt.netContent || p?.netContent || pProduct?.netContent || product?.netContent || null,
          };
        })
        .filter((x: any) => x.inStock >= 1)
    : [];

  const fallbackGroupAvailability = groupProducts
    .flatMap((gp: any) => (Array.isArray(gp?.availabilities) ? gp.availabilities : []))
    .find((a: any) => Number(a?.inStock || 0) >= 1) || null;

  const fallbackSizeOption = sizeOptions.find((opt: any) => Number(opt?.inStock || 0) >= 1) || null;

  const avail =
    baseAvail ||
    fallbackGroupAvailability ||
    (fallbackSizeOption
      ? {
          id: product.id,
          productId: fallbackSizeOption.productId || product.id,
          covaLocationId: storeId || 0,
          packageId: fallbackSizeOption.packageId || product.id,
          inStock: Number(fallbackSizeOption.inStock || 0),
          price: Number(fallbackSizeOption.price || 0),
          unitCost: Number(fallbackSizeOption.unitCost || fallbackSizeOption.price || 0),
          compareAtPrice:
            fallbackSizeOption.compareAtPrice !== null && fallbackSizeOption.compareAtPrice !== undefined
              ? Number(fallbackSizeOption.compareAtPrice)
              : null,
        }
      : null);

  if (!avail) return null;

  const price =
    avail.price !== null && avail.price !== undefined ? Number(avail.price) : Number(avail.unitCost || 0);

  // Enhance groupProducts with availability data for ProductCard variant parsing
  const enhancedGroupProducts = groupProducts.length > 0
    ? groupProducts.map((gp: any) => {
        const gpProduct = gp?.product || gp;
        let gpAvail = null;
        if (gp?.availabilities) {
          gpAvail = Array.isArray(gp.availabilities) && gp.availabilities.length > 0
            ? (storeId
                ? gp.availabilities.find((a: any) => a.covaLocationId === storeId) || gp.availabilities[0]
                : gp.availabilities[0])
            : null;
        }
        
        return {
          id: gp.id || gp.productId,
          productId: gp.productId || gp.id,
          name: gpProduct?.name || gp.name,
          slug: gpProduct?.slug || gp.slug,
          heroShotUri: gpProduct?.heroShotUri || gp.heroShotUri,
          brandName: gpProduct?.brandName || gp.brandName,
          classificationName: gpProduct?.classificationName || gp.classificationName,
          shortDescription: gpProduct?.shortDescription || gp.shortDescription,
          longDescription: gpProduct?.longDescription || gp.longDescription,
          sizeOrWeight: gp.sizeOrWeight || null,
          masterProductId: gpProduct?.masterProductId || gp.masterProductId,
          masterProductName: gpProduct?.masterProductName || gp.masterProductName,
          ...(gpAvail ? {
            availabilities: [gpAvail],
          } : {}),
        };
      })
    : [];

  // For merch/clothing/apparel: backend sizeOptions can be collapsed (e.g. only one "XL")
  // because grouping may de-dupe by sizeOrWeight. Admin group-view uses groupProducts; we should too.
  const classification = (product?.classificationName || '').toLowerCase();
  const isMerchLike =
    classification.includes('clothing') ||
    classification.includes('merch') ||
    classification.includes('apparel') ||
    classification.includes('accessories');

  if (isMerchLike && enhancedGroupProducts.length > 0) {
    const COLOR_KEYWORDS = [
      'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 'black', 'white', 'gray', 'grey',
      'brown', 'beige', 'tan', 'navy', 'maroon', 'teal', 'cyan', 'magenta', 'lime', 'olive',
      'silver', 'gold', 'bronze', 'copper', 'ivory', 'cream', 'coral', 'salmon', 'turquoise',
      'violet', 'indigo', 'amber', 'burgundy', 'charcoal', 'jet black', 'midnight black', 'clear'
    ];
    const isColorValue = (value: string): boolean => {
      const lower = value.toLowerCase().trim();
      return COLOR_KEYWORDS.some(keyword => lower === keyword || lower.includes(keyword));
    };

    const derived = enhancedGroupProducts
      .map((gp: any) => {
        const gpAvail = Array.isArray(gp?.availabilities) && gp.availabilities.length > 0 ? gp.availabilities[0] : null;
        if (!gpAvail) return null;

        // Prefer explicit sizeOrWeight; otherwise try to parse from the original variant name
        let sizeOrWeight: string | null = gp.sizeOrWeight || null;
        if (!sizeOrWeight && gp?.name) {
          try {
            const parsed = parseProduct(
              String(gp.name),
              product?.classificationName || null,
              product?.brandName || null,
              null
            );
            // Typical for clothing: variant1=color, variant2=size
            const candidate = parsed.variant2 || parsed.variant1 || null;
            if (candidate && !isColorValue(candidate)) {
              sizeOrWeight = candidate;
            }
          } catch {
            // ignore parse failures
          }
        }

        const derivedPrice =
          gpAvail.price !== null && gpAvail.price !== undefined
            ? Number(gpAvail.price)
            : Number(gpAvail.unitCost || 0);

        return {
          packageId: gpAvail.packageId || avail.packageId,
          sizeOrWeight,
          price: derivedPrice,
          compareAtPrice: gpAvail.compareAtPrice ? Number(gpAvail.compareAtPrice) : null,
          unitCost: gpAvail.unitCost ? Number(gpAvail.unitCost) : null,
          inStock: Number(gpAvail.inStock || 0),
          productId: gp.productId || gp.id,
          variationId: gp.variationId || null,
          productSlug: gp.slug || product?.slug || null,
          heroShotUri: gp.heroShotUri || product?.heroShotUri || null,
          shortDescription: gp.shortDescription || product?.shortDescription || null,
          longDescription: gp.longDescription || product?.longDescription || null,
          netContent: gp.netContent || product?.netContent || null,
        };
      })
      .filter(Boolean)
      .filter((x: any) => Number(x.inStock || 0) >= 1);

    // Prefer derived options when they contain more distinct variants than backend-provided sizeOptions.
    if (derived.length > sizeOptions.length) {
      sizeOptions = derived as any[];
    }
  }

  // Get the correct display product name - backend should already format as "ProductName - Brand"
  // For grouped products, use the name from backend (already formatted) or parse if needed
  let displayProductName = product.name;
  let displayBrand = product.brandName || null;
  
  // Check if the backend already formatted it as "ProductName - Brand"
  const alreadyFormatted = displayProductName && displayProductName.includes(' - ') && displayProductName !== product.classificationName;
  
  if (!alreadyFormatted) {
    // Backend didn't format it, so we need to format it on the client side
    // Try to use masterProductName first (if available and different from classification)
    if (product.masterProductName && product.masterProductName !== product.classificationName) {
      displayProductName = product.masterProductName;
    } else {
      // Parse the product name to get the actual product name (not classification)
      // Use the first groupProduct's name if available to get a better sample
      const nameToParse = enhancedGroupProducts.length > 0 
        ? (enhancedGroupProducts[0].name || product.name)
        : product.name;
      
      try {
        const parsed = parseProduct(
          nameToParse,
          product.classificationName || null,
          product.brandName || null,
          null // Don't include sizeOrWeight when parsing for display name
        );
        
        // Use parsed product name if it's different from classification
        if (parsed.productName && parsed.productName !== product.classificationName) {
          displayProductName = parsed.productName;
        }
        
        // Use parsed brand if available
        if (parsed.brand && !displayBrand) {
          displayBrand = parsed.brand;
        }
      } catch (error) {
        // If parsing fails, fall back to existing name
        console.warn('Failed to parse product name:', error);
      }
    }
    
    // Format as "ProductName - Brand" to match admin panel group view
    // Only add brand if we have both product name and brand, and product name is not just the classification
    if (displayProductName && displayBrand && displayProductName !== product.classificationName) {
      // Only append brand if it's not already included in the product name
      if (!displayProductName.toLowerCase().includes(displayBrand.toLowerCase())) {
        displayProductName = `${displayProductName} - ${displayBrand}`;
      }
    }
  }

  const mergedRoot = mergeCannabisFieldsFromAvailability(product, avail);

  return {
    item: {
      id: avail.id || product.id,
      productId: product.id,
      covaLocationId: storeId || avail.covaLocationId || 0,
      packageId: avail.packageId || product.id,
      inStock: Number(avail.inStock || 0),
      unitCost: price,
      compareAtPrice: avail.compareAtPrice ? Number(avail.compareAtPrice) : null,
      ...(sizeOptions.length > 0 ? { sizeOptions } : {}),
      ...(enhancedGroupProducts.length > 0 ? { groupProducts: enhancedGroupProducts } : {}),
      product: {
        ...(mergedRoot || {}),
        id: product.id,
        name: displayProductName,
        heroShotUri: product.heroShotUri,
        classificationName: product.classificationName,
        brandName: product.brandName,
        slug: product.slug,
        shortDescription: product.shortDescription,
        longDescription: product.longDescription,
        masterProductName: product.masterProductName,
        // Include all fields needed for filtering
        thcPercent: mergedRoot.thcPercent,
        thcMg: mergedRoot.thcMg,
        thcMin: mergedRoot.thcMin,
        thcMax: mergedRoot.thcMax,
        cbdPercent: mergedRoot.cbdPercent,
        cbdMg: mergedRoot.cbdMg,
        cbdMin: mergedRoot.cbdMin,
        cbdMax: mergedRoot.cbdMax,
        cbnPercent: mergedRoot.cbnPercent,
        cbnMg: mergedRoot.cbnMg,
        cbgPercent: mergedRoot.cbgPercent,
        cbgMg: mergedRoot.cbgMg,
        totalTerpenePercent: mergedRoot.totalTerpenePercent,
        pieces: mergedRoot.pieces,
        netContent: mergedRoot.netContent,
        availabilities: product.availabilities,
      },
    },
    price,
  };
}

export function ProductShowcaseContent({
  slot,
  className = '',
  carouselDesktop = false,
}: {
  slot?: ShowcaseSlot;
  className?: string;
  /** Horizontal scroll strip + header arrows (Why Dank product selection mock). */
  carouselDesktop?: boolean;
}) {
  const { state } = useCart();
  const storeId = state.storeCovaLocationId || getStoredStoreId();

  // Extract data for hooks (before early returns)
  const rawProducts = Array.isArray(slot?.products) ? slot.products : [];
  const productsByThreshold = (slot as any)?.productsByThreshold as
    | Record<string, any[]>
    | undefined;
  const title = slot?.title || 'Product Selection';
  const cols = 'grid-cols-2 sm:grid-cols-4';
  /** Two rows is a fixed block of eight; one row pages four at a time. */
  const singleRow = slot?.layout !== '2x4';
  const [page, setPage] = useState(0);
  const filterGroup = slot?.filterGroup;

  // Get filter field label helper (matching admin preview)
  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      PRICE: '$',
      THC_PERCENT: 'THC %',
      THC_MG: 'THC mg',
      THC_MIN: 'THC Min %',
      THC_MAX: 'THC Max %',
      CBD_PERCENT: 'CBD %',
      CBD_MG: 'CBD mg',
      CBD_MIN: 'CBD Min %',
      CBD_MAX: 'CBD Max %',
      CBN_PERCENT: 'CBN %',
      CBN_MG: 'CBN mg',
      CBG_PERCENT: 'CBG %',
      CBG_MG: 'CBG mg',
      TOTAL_TERPENE_PERCENT: 'Terpene %',
      PACK_SIZE: 'Pieces',
      INVENTORY: 'Stock',
    };
    return labels[field] || field;
  };

  // Generate filter pills from filterGroup configuration
  const filterPills = useMemo(() => {
    if (!filterGroup || !Array.isArray(filterGroup.thresholds) || filterGroup.thresholds.length === 0) {
      return [];
    }
    const field = filterGroup.field || 'PRICE';
    const label = getFieldLabel(field);
    const prefix = 'Under ';
    
    return [...filterGroup.thresholds]
      .sort((a, b) => b - a) // Sort descending (highest first)
      // A band the selected store has nothing in would render a pill that
      // empties the row when clicked. Smaller shops carry a fraction of the
      // range, so the same band is worth showing at one store and dead at
      // another; drop it where the API came back with nothing.
      .filter((threshold) => {
        if (!productsByThreshold) return true;
        const list = productsByThreshold[String(threshold)];
        return !Array.isArray(list) || list.length > 0;
      })
      .map((threshold) => ({
        threshold,
        label: `${prefix}${label}${threshold}`,
      }));
  }, [filterGroup, productsByThreshold]);

  // Set initial priceCap
  const [priceCap, setPriceCap] = useState<number>(49);

  // When backend provides per-threshold lists, use the active pill's list directly.
  const productsForActiveCap = useMemo(() => {
    if (productsByThreshold && Number.isFinite(priceCap)) {
      const list = productsByThreshold[String(priceCap)];
      if (Array.isArray(list)) return list;
    }
    return rawProducts;
  }, [productsByThreshold, priceCap, rawProducts]);

  // Select the first pill that is actually on screen. Following
  // filterGroup.thresholds instead would start on a band the store has nothing
  // in, showing an empty row under no highlighted pill.
  useEffect(() => {
    if (filterPills.length > 0) {
      setPriceCap(filterPills[0].threshold);
    } else {
      setPriceCap(49); // No filter group, or no band with stock behind it.
    }
  }, [filterPills]);

  // All hooks must be called before any early returns
  const mapped = useMemo(() => {
    if (!slot || !slot.collectionId || productsForActiveCap.length === 0) return [];
    return productsForActiveCap
      .map((p: any) => toCardItem(p, storeId))
      .filter(Boolean) as Array<{ item: any; price: number }>;
  }, [productsForActiveCap, storeId, slot]);

  const filtered = useMemo(() => {
    if (mapped.length === 0) return [];
    
    // Two rows shows eight and stops there. One row shows four at a time and
    // pages through the rest, so it keeps a deeper pool to page over rather
    // than throwing everything past the fourth product away.
    const layoutLimit = slot?.layout === '2x4' ? 8 : SINGLE_ROW_POOL;
    
    if (!filterGroup) {
      // No filter group = show all, but limit to layout count
      return mapped.slice(0, layoutLimit);
    }
    
    // Apply filter based on configured field
    const field = filterGroup.field || 'PRICE';
    if (!Number.isFinite(priceCap)) {
      // No active filter: limit to layout count
      return mapped.slice(0, layoutLimit);
    }
    
    // Step 1: for the active cap, compute the "best" field value for each product.
    // Step 2: filter by threshold (<= cap).
    // Step 3: sort DESC (highest -> lowest) by that best value.
    // Step 4: take first 4/8.
    //
    // For PRICE: "best" means the highest price that is still <= cap (closest to cap).
    const getFieldValueForCap = (x: { item: any; price: number }): { v: number; bestSizeIdx: number } => {
      if (field === 'PRICE') {
        let best = Number.NEGATIVE_INFINITY;
        let bestIdx = -1; // -1 means base price
        if (Number.isFinite(x.price) && x.price > 0 && x.price <= priceCap) {
          best = x.price;
          bestIdx = -1;
        }
        const opts = Array.isArray(x.item?.sizeOptions) ? x.item.sizeOptions : [];
        for (let i = 0; i < opts.length; i++) {
          const opt = opts[i];
          const p = typeof opt?.price === 'number' ? opt.price : Number(opt?.price || 0);
          if (Number.isFinite(p) && p > 0 && p <= priceCap && p > best) {
            best = p;
            bestIdx = i;
          }
        }
        return { v: best, bestSizeIdx: bestIdx };
      }

      const product = x.item?.product;
      if (!product) return { v: Number.NEGATIVE_INFINITY, bestSizeIdx: -1 };

      let value: number | null = null;
      if (field === 'THC_PERCENT') value = product.thcPercent ? Number(product.thcPercent) : null;
      else if (field === 'THC_MG') value = product.thcMg ? Number(product.thcMg) : null;
      else if (field === 'THC_MIN') value = product.thcMin ? Number(product.thcMin) : null;
      else if (field === 'THC_MAX') value = product.thcMax ? Number(product.thcMax) : null;
      else if (field === 'CBD_PERCENT') value = product.cbdPercent ? Number(product.cbdPercent) : null;
      else if (field === 'CBD_MG') value = product.cbdMg ? Number(product.cbdMg) : null;
      else if (field === 'CBD_MIN') value = product.cbdMin ? Number(product.cbdMin) : null;
      else if (field === 'CBD_MAX') value = product.cbdMax ? Number(product.cbdMax) : null;
      else if (field === 'CBN_PERCENT') value = product.cbnPercent ? Number(product.cbnPercent) : null;
      else if (field === 'CBN_MG') value = product.cbnMg ? Number(product.cbnMg) : null;
      else if (field === 'CBG_PERCENT') value = product.cbgPercent ? Number(product.cbgPercent) : null;
      else if (field === 'CBG_MG') value = product.cbgMg ? Number(product.cbgMg) : null;
      else if (field === 'TOTAL_TERPENE_PERCENT') value = product.totalTerpenePercent ? Number(product.totalTerpenePercent) : null;
      else if (field === 'PACK_SIZE') value = product.pieces ? Number(product.pieces) : null;
      else if (field === 'INVENTORY') {
        if (x.item?.sizeOptions && Array.isArray(x.item.sizeOptions) && x.item.sizeOptions.length > 0) {
          const total = x.item.sizeOptions.reduce((sum: number, opt: any) => sum + Number(opt?.inStock || 0), 0);
          value = Number.isFinite(total) && total >= 0 ? total : null;
        } else if (product.availabilities && Array.isArray(product.availabilities)) {
          const total = product.availabilities.reduce((sum: number, a: any) => sum + Number(a.inStock || 0), 0);
          value = Number.isFinite(total) && total >= 0 ? total : null;
        } else {
          value = x.item?.inStock ? Number(x.item.inStock) : null;
        }
      }

      return { v: value === null || Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value, bestSizeIdx: -1 };
    };

    // Normalize (PRICE only): reorder sizeOptions so the default selection matches the "best <= cap" variant.
    const normalized =
      field === 'PRICE'
        ? mapped.map((x) => {
            const { v, bestSizeIdx } = getFieldValueForCap(x);
            const opts = Array.isArray(x.item?.sizeOptions) ? x.item.sizeOptions : [];
            if (bestSizeIdx >= 0) {
              const nextOpts = [...opts];
              const [best] = nextOpts.splice(bestSizeIdx, 1);
              nextOpts.unshift(best);
              return {
                item: { ...x.item, sizeOptions: nextOpts },
                price: Number.isFinite(v) && v > 0 ? v : x.price,
              };
            }
            return { ...x, price: Number.isFinite(v) && v > 0 ? v : x.price };
          })
        : mapped;

    const filteredSorted = normalized
      .map((x, idx) => {
        const { v } = getFieldValueForCap(x);
        return { x, idx, v };
      })
      .filter((o) => o.v <= priceCap && Number.isFinite(o.v) && o.v !== Number.NEGATIVE_INFINITY)
      // DESC (highest -> lowest)
      .sort((a, b) => (b.v - a.v) || (a.idx - b.idx))
      .map((o) => o.x);

    return filteredSorted.slice(0, layoutLimit);
  }, [mapped, priceCap, filterGroup, slot]);

  const desktopStripRef = useRef<HTMLDivElement>(null);
  const scrollDesktopBy = useCallback((direction: 'left' | 'right') => {
    const el = desktopStripRef.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.85, 720);
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  if (!slot || !slot.collectionId) return null;
  if (filtered.length === 0) return null;

  const shopAllLink =
    slot.collectionHandle != null && slot.collectionHandle !== '' ? (
      <Button asChild variant="primary" className={`${ROW_CONTROL} px-5`}>
        <Link href={`/collections/${slot.collectionHandle}`}>Shop all</Link>
      </Button>
    ) : (
      <Button type="button" disabled variant="primary" className={`${ROW_CONTROL} px-5`}>
        Shop all
      </Button>
    );

  /* Same segmented control as the header's Pickup/Delivery switcher, styled
     from ui/segmented so the two cannot drift. Segments must be `raw`: the
     primary and ghost variants apply HOVER_NAVY, which filled an inactive
     option on hover and squared off the selected one. */
  const filterPillRow = (
    <div className={SEGMENTED_TRACK}>
      {filterPills.map((pill) => {
        const active = priceCap === pill.threshold;
        return (
          <Button
            raw
            key={pill.threshold}
            type="button"
            aria-pressed={active}
            data-no-lift
            onClick={() => setPriceCap(pill.threshold)}
            className={segmentClasses(active)}
          >
            {pill.label}
          </Button>
        );
      })}
    </div>
  );

  /** The four on screen now. Two rows shows its whole block. */
  const pageCount = singleRow ? Math.max(1, Math.ceil(filtered.length / ROW_SIZE)) : 1;
  const visible = singleRow ? filtered.slice(page * ROW_SIZE, page * ROW_SIZE + ROW_SIZE) : filtered;

  // Changing the price filter rebuilds the pool, so an old page index could
  // land past the end and show an empty row.
  useEffect(() => {
    setPage(0);
  }, [priceCap, slot?.layout, filtered.length]);

  /**
   * A square white arrow, the shape the design draws beside Shop all. Same
   * height as Shop all so the row reads as one set of controls.
   */
  const arrowButton = (
    direction: 'left' | 'right',
    label: string,
    disabled: boolean,
    onClick: () => void
  ) => (
    <Button
      raw
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      /* raw, not the ghost variant: ghost forces a navy fill on hover, which
         put a dark chevron on a dark button. These stay white and only darken
         their border. */
      className={
        `${ROW_CONTROL} aspect-square inline-flex items-center justify-center border border-neutral-15 ` +
        'bg-constant-white text-text-primary transition-colors duration-200 ease-out ' +
        'hover:border-neutral-40 disabled:cursor-not-allowed disabled:opacity-40'
      }
    >
      {direction === 'left' ? <ChevronLeftIcon size={18} /> : <ChevronRightIcon size={18} />}
    </Button>
  );

  const rowPager = pageCount > 1 ? (
    <div className="flex items-center gap-1">
      {arrowButton('left', 'Previous products', page === 0, () =>
        setPage((p) => Math.max(0, p - 1))
      )}
      {arrowButton('right', 'More products', page >= pageCount - 1, () =>
        setPage((p) => Math.min(pageCount - 1, p + 1))
      )}
    </div>
  ) : null;

  const desktopCarouselNav = (
    <div className="hidden items-center gap-1 sm:flex">
      {arrowButton('left', 'Scroll products left', false, () => scrollDesktopBy('left'))}
      {arrowButton('right', 'Scroll products right', false, () => scrollDesktopBy('right'))}
    </div>
  );

  return (
    <section className={`px-4 sm:px-6 lg:px-8 ${className}`.trim()} aria-label={title}>
      {carouselDesktop ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <h2 className="text-h2-mobile md:text-h3-desktop font-bold text-text-primary">{title}</h2>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              {desktopCarouselNav}
              <div className="max-sm:hidden">{shopAllLink}</div>
            </div>
          </div>

          {filterPills.length > 0 ? (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {filterPillRow}
              <div className="sm:hidden">{shopAllLink}</div>
            </div>
          ) : (
            <div className="mt-4 flex justify-end sm:hidden">{shopAllLink}</div>
          )}

          {/* Mobile: Horizontal scrolling */}
          <div className="mt-4 overflow-x-auto scrollbar-hide sm:hidden">
            <div className="flex w-max gap-2">
              {filtered.map((p, index) => (
                <div key={`${p.item.id}-${index}`} className="w-[282px] flex-shrink-0">
                  <ProductCard item={p.item as any} price={p.price} homepage={true} />
                </div>
              ))}
            </div>
          </div>

          {/* Desktop: carousel strip */}
          <div
            ref={desktopStripRef}
            className="mt-4 hidden gap-4 overflow-x-auto pb-1 scrollbar-hide sm:flex"
          >
            <div className="flex w-max gap-4 pt-1">
              {filtered.map((p, index) => (
                <div key={`desktop-${p.item.id}-${index}`} className="w-[282px] shrink-0">
                  <ProductCard item={p.item as any} price={p.price} homepage={true} />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-h2-mobile md:text-h3-desktop font-bold text-text-primary">{title}</h2>
            {/* A slot with no price filter has no second row to hang the pager
                and Shop all on, so they sit beside the title instead. Without
                this a one-row slot with no filter group showed no arrows. */}
            {filterPills.length === 0 ? (
              <div className="hidden items-center gap-2 sm:flex">
                {rowPager}
                {shopAllLink}
              </div>
            ) : null}
          </div>

          {filterPills.length > 0 && (
            <div className="mt-4 flex items-center justify-between gap-4">
              {filterPillRow}

              <div className="flex items-center gap-2">
                {rowPager}
                {shopAllLink}
              </div>
            </div>
          )}

          {/* Mobile: Horizontal scrolling */}
          <div className="mt-4 overflow-x-auto scrollbar-hide sm:hidden">
            <div className="flex w-max gap-2">
              {filtered.map((p, index) => (
                <div key={`${p.item.id}-${index}`} className="w-[282px] flex-shrink-0">
                  <ProductCard item={p.item as any} price={p.price} homepage={true} />
                </div>
              ))}
            </div>
          </div>

          {/* Desktop: Grid layout */}
          <div className={`mt-4 hidden gap-4 sm:grid ${cols}`}>
            {visible.map((p, index) => (
              <ProductCard key={`${p.item.id}-${index}`} item={p.item as any} price={p.price} homepage={true} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default function ProductShowcaseSection({
  slotKey,
  className = '',
  carouselDesktop = false,
}: {
  slotKey: SlotKey;
  className?: string;
  /**
   * Opt in to the scrolling strip of fixed-width cards. Off by default: every
   * slot draws the same four-column grid, so a card is the same size wherever
   * it appears.
   *
   * This used to switch itself on for any slot named productSelection, which
   * only Why Dank has. Its cards came out 282px wide and overflowed the row
   * while the identical section on the homepage stretched to fit.
   */
  carouselDesktop?: boolean;
}) {
  const { slotsByKey, loading } = usePageShowcases();
  const slot = slotsByKey[slotKey] as ShowcaseSlot | undefined;

  if (loading) return null;

  return (
    <ProductShowcaseContent slot={slot} className={className} carouselDesktop={carouselDesktop} />
  );
}


